import React, { useState, useRef, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import SearchBar from './SearchBar';
import FloatingLogo, { LOGO_IMAGES } from './FloatingLogo';

interface FloatingLogoInstance {
  id: number;
  x: number;
  y: number;
  imageIndex: number;
}

export default function Landing() {
  const { t } = useTranslation();
  const [floatingLogo, setFloatingLogo] = useState<FloatingLogoInstance | null>(null);
  const [lastImageIndex, setLastImageIndex] = useState(-1);
  const [nextId, setNextId] = useState(0);
  const landingSectionRef = useRef<HTMLDivElement>(null);
  const throttleRef = useRef(false);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (throttleRef.current) return;
    
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('input') ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]') ||
      target.getAttribute('data-no-float') === 'true' ||
      target.closest('[data-no-float="true"]')
    ) {
      return;
    }

    throttleRef.current = true;
    setTimeout(() => {
      throttleRef.current = false;
    }, 100);

    let newImageIndex;
    do {
      newImageIndex = Math.floor(Math.random() * LOGO_IMAGES.length);
    } while (newImageIndex === lastImageIndex);

    const x = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const y = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

    setLastImageIndex(newImageIndex);
    setFloatingLogo({
      id: nextId,
      x,
      y,
      imageIndex: newImageIndex
    });
    setNextId(prev => prev + 1);
  }, [lastImageIndex, nextId]);

  const removeLogo = useCallback(() => {
    setFloatingLogo(null);
  }, []);

  return (
    <div className="relative min-h-[100dvh] bg-dark overflow-hidden hardware-accelerated">
      <Header />
      
      <div className="absolute inset-0">
        {/* Primary Glow */}
        <div className="absolute top-1/4 left-1/4 w-[200px] sm:w-[300px] lg:w-[600px] 
          h-[200px] sm:h-[300px] lg:h-[600px] bg-primary/20 rounded-full 
          blur-[80px] sm:blur-[100px] animate-pulse mix-blend-screen hardware-accelerated"></div>
        
        {/* Secondary Glow */}
        <div className="absolute bottom-1/3 right-1/3 w-[250px] sm:w-[350px] lg:w-[700px] 
          h-[250px] sm:h-[350px] lg:h-[700px] bg-primary/10 rounded-full 
          blur-[100px] sm:blur-[120px] animate-pulse delay-700 mix-blend-screen hardware-accelerated"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,theme(colors.primary/0.5)_1px,transparent_1px),linear-gradient(-45deg,theme(colors.primary-light/0.5)_1px,transparent_1px)]"
               style={{ backgroundSize: '20px 20px', transform: 'perspective(500px) rotateX(60deg)' }}></div>
        </div>
        
        {/* Animated Stars */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-primary rounded-full animate-twinkle hardware-accelerated"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              transform: `scale(${Math.random() * 1.5 + 0.5})`
            }}
          ></div>
        ))}
      </div>

      <div 
        ref={landingSectionRef}
        className="relative z-10 flex flex-col items-center justify-center min-h-[100dvh] text-center 
          px-3 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-0 hardware-accelerated"
        onClick={handleClick}
        onTouchStart={handleClick}
      >
        <div className="space-y-4 sm:space-y-8 w-full max-w-[90rem] mx-auto">
          <div className="relative">
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold mb-2 sm:mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r 
                from-primary via-primary-light to-primary animate-gradient-x">
                {t('landing.discover')}
              </span>
            </h1>
            <div className="relative inline-block transform transition-transform duration-300 ease-out">
              <div className="text-3xl sm:text-5xl lg:text-7xl font-bold px-3 sm:px-6 lg:px-8 
                py-2 sm:py-3 lg:py-4 bg-dark-lighter rounded-2xl border border-primary/20 
                shadow-[0_0_50px_rgba(255,85,0,0.1)] hover:shadow-[0_0_100px_rgba(255,85,0,0.2)] 
                transition-all duration-500 ease-out hardware-accelerated">
                {t('landing.nodeRocks')}
              </div>
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold mt-2 sm:mt-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r 
                from-white via-primary-light to-primary">
                {t('landing.onBitcoin')}
              </span>
            </h1>
          </div>

          <SearchBar />
        </div>

        <button className="mt-6 sm:mt-12 lg:mt-16 text-primary opacity-75 hover:opacity-100 
          transition-all duration-300 hover:transform hover:translate-y-1">
          <ChevronDown className="w-6 sm:w-8 lg:w-10 h-6 sm:h-8 lg:h-10 animate-bounce" />
        </button>
      </div>

      {floatingLogo && (
        <FloatingLogo
          key={floatingLogo.id}
          x={floatingLogo.x}
          y={floatingLogo.y}
          imageIndex={floatingLogo.imageIndex}
          onAnimationComplete={removeLogo}
        />
      )}
    </div>
  );
}